<style lang="scss" scoped>
.result-small {
    font-size: 1rem;
    white-space: normal;
}

.result-medium {
    font-size: 1.5rem;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
}

.result-large {
    font-size: 3rem;
    white-space: nowrap;
}

.missing-result {
    font-size: 2rem;
}

.team-image {
    width: 75px;
    height: 75px;
}

.team-name {
    font-size: 1.2rem;
    font-weight: bold;
}

.name {
    font-size: 1.5rem;
    font-weight: bold;
}

@media screen and (max-width: 450px) {
    .team-image {
        width: 50px;
        height: 50px;
    }
    .result, .missing-result {
        font-size: 1.5rem;
        white-space: normal;
    }
    .team-name, .name {
        font-size: 1rem;
    }
}

.mycontainer {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    justify-items: center;
    gap: 10px;
}

.midsection, .sidesection {
    text-align: center;
}

@media screen and (min-width: 450px) {
    .team-image {
        width: 100px;
        height: 100px;
    }
}

</style>

<template>
    <v-card v-if="!match" :flat="flat">
        <v-card-text>
            {{$translate(noMatchText)}}
        </v-card-text>
    </v-card>
    <div v-else-if="loading">
        <v-progress-circular size="20" indeterminate color="primary" width="2"></v-progress-circular>
    </div>
    <v-card v-else-if="twoTeams" :flat="flat" ref="vcard">
        <div v-if="match.name" class="align-center justify-center name d-flex">{{match.name}}</div>
        <div v-if="match.gameclass_name" class="text-center text-caption text--secondary mb-1">{{ match.gameclass_name }}<span v-if="match.division_name"> - {{ match.division_name }}</span></div>
        <div class="mycontainer">
            <div class="sidesection">
                <img :src="'/api/public/image/get?size=200x200&type=team&id=' + match.teams[0].id" class="team-image" />
                <div v-if="showTeamNames" class="team-name">{{match.teams[0].name}}</div>
                <div v-if="showHomeAway">{{$translate('Home')}}</div>
            </div>
            <div class="midsection">
                <template v-if="match.finished || showLiveResult">
                    <template v-if="isSetResult">
                        <div :class="`result ${resultSizeClass}`">{{ setsScore }}</div>
                        <div v-if="setWinnerName" class="result-small text--secondary">{{ $translate('Winner') }}: {{ setWinnerName }}</div>
                        <div v-else class="result-small text--secondary">{{ result }}</div>
                    </template>
                    <div v-else :class="`result ${resultSizeClass}`">{{result}}</div>
                </template>
                <div class="missing-result" v-else>{{$translate('Missing result')}}</div>
                <div v-if="match.activity">{{Date.formatFullLong(match.activity.startTime).capitalize()}}</div>
                <div v-if="showLocation && location">{{ location }}</div>
                <slot name="append-center-content"></slot>
            </div>
            <div class="sidesection">
                <img :src="'/api/public/image/get?size=200x200&type=team&id=' + match.teams[1].id" class="team-image" />
                <div v-if="showTeamNames" class="team-name">{{match.teams[1].name}}</div>
                <div v-if="showHomeAway">{{$translate('Away')}}</div>
            </div>
        </div>
    </v-card>
    <v-card v-else class="d-flex flex-column text-center" :flat="flat">
        <div v-if="match.name" class="font-weight-bold">{{match.name}}</div>
        <div class="result" v-if="ourResult">{{ourResult.result}}</div>
        <!-- FIXME Show all results if teamId is not provided-->
        <div>{{Date.formatFullLong(match.activity.startTime).capitalize()}}</div>
    </v-card>
</template>

<script>
import { isSetResult as isSetResultType, getSetsScore as getSetsScoreFor } from '../util';
export default {
    name: 'zoezi-teamsport-match-result',

    props: {
        match: {
            type: Object
        },
        teamId: {
            type: Number
        },
        noMatchText: {
            type: String,
            default: 'No match found'
        },
        flat: {
            type: Boolean,
            default: false
        },
        showTeamNames: {
            type: Boolean,
            default: true
        },
        showHomeAway: {
            type: Boolean,
            default: true
        },
        showLiveResult: {
            type: Boolean,
            default: false
        },
        showLocation: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            loading: false,
            invalidResults: [],
            unsubscribe: null,
            cardSize: 'medium'
        }
    },

    computed: {
        isSetResult() {
            return isSetResultType(this.match);
        },
        setWinnerName() {
            const r0 = this.match?.result?.[0];
            if (r0?.invalid_id && r0?.team_id) {
                const t = this.match.teams?.find(x => x.id === r0.team_id);
                return t ? t.name : '';
            }
            return '';
        },
        setsScore() {
            return getSetsScoreFor(this.match, this.invalidResults);
        },
        result() {
            if (!this.match) {
                return '';
            }

            if (['padel', 'sets'].includes(this.match.result_type)) {
                return this.match.result?.[0]?.result ?? '-';
            }

            const getTeamResult = (teamId) => {
                const resultObject = this.match.result?.find(r => r.team_id === teamId);
                if (resultObject) {
                    if (resultObject.invalid_id) {
                        const invalidResult = this.invalidResults?.find(r => r.id === resultObject.invalid_id);
                        return invalidResult ? invalidResult.name : '0';
                    }
                    // Return the result if present, default to '0' otherwise
                    return resultObject.result || '0';
                }
                return '0';
            };

            const [homeTeamId, awayTeamId] = this.match.teams.map(team => team.id);

            const homeResult = getTeamResult(homeTeamId);
            const awayResult = getTeamResult(awayTeamId);

            return `${homeResult} - ${awayResult}`;
        },
        ourResult() {
            return this.match?.result?.find(r => r.team_id == this.teamId);
        },
        twoTeams() {
            return this.match?.teams?.length == 2;
        },
        location() {
            if (!this.match || !this.match.activity) {
                return null;
            }
            let workout = this.match.activity;
            if (workout.location || workout.room) {
                return (workout.room ? workout.room.name : '') + (workout.location ? ', ' + workout.location.name : '');
            }
            return null;
        },
        resultSizeClass() {
            return `result-${this.cardSize}`;
        },
    },

    methods: {
        getInvaldResults() {
            this.loading = true;
            this.$api.get('/api/teamsport/resultinvalid/get').then(response => {
                this.invalidResults = response;
                this.loading = false;
            });
        },
        updateCardSize() {
            this.$nextTick(() => {
                if (this.$refs.vcard?.$el?.offsetWidth) {
                    const width = this.$refs.vcard?.$el?.offsetWidth;
                    if (width < 300) {
                        this.cardSize = 'small';
                    } else if (width >= 300 && width < 600) {
                        this.cardSize = 'medium';
                    } else {
                        this.cardSize = 'large';
                    }
                }
            });
        },
    },

    mounted() {
        this.getInvaldResults();
        this.unsubscribe = this.$api.subscribe({
            type: 'teamsportresultinvalid',
            callback: () => this.getInvalidResults()
        })

        this.updateCardSize();
        window.addEventListener('resize', this.updateCardSize);
    },

    destroyed() {
        this.unsubscribe();
        window.removeEventListener('resize', this.updateCardSize);
    }
}
</script>
